import { db } from './db.js';
import { repository } from './repository.js';
import { AttachmentTooLargeError, MAX_ATTACHMENT_BYTES, putAttachment } from './attachments.js';
import { DATA_TABLES } from './backup.js';
/** The first observation and its profile either both exist, or neither exists. */
export async function createPetWithFirstObservation(input) {
    const name = input.name.trim();
    const fact = input.fact.trim();
    if (!name || !fact)
        throw new Error('아이 이름과 오늘 본 사실을 입력해 주세요.');
    return db.transaction('rw', [db.pets, db.events, db.settings], async () => {
        const pet = await repository.put('pets', { name, species: input.species, sex: 'unknown', neutered: false, birthYearOnly: false, conditions: [], sortOrder: Date.now() });
        const event = await repository.put('events', { petId: pet.id, type: 'other', occurredAt: Date.now(), severityNote: fact, payload: { type: 'other' } });
        await repository.setSetting('activePetId', pet.id);
        return { pet, event };
    });
}
/** Keep selected media and the record referencing it in the same local commit. */
export async function withRecordAttachments(table, petId, files, write) {
    const selected = files.slice(0, 3);
    if (selected.some((file) => file.size > MAX_ATTACHMENT_BYTES))
        throw new AttachmentTooLargeError();
    return db.transaction('rw', [db.pets, db.attachments, db[table]], async () => {
        const pet = await db.pets.get(petId);
        if (!pet || pet.deletedAt)
            throw new Error('아이의 현재 기록을 다시 확인해 주세요. 첨부는 저장하지 않았어요.');
        const ids = [];
        for (const blob of selected)
            ids.push((await putAttachment({ petId, blob })).id);
        return write(ids);
    });
}
/** Release this attachment's live bytes and references atomically, keeping originals and recovery generations. */
export async function purgeRecordAttachment(id, expectedRevision) {
    return db.transaction('rw', [db.attachments, db.trash, ...DATA_TABLES.map(table => db[table])], async () => {
        const attachment = await db.attachments.get(id);
        if (!attachment || !attachment.deletedAt || attachment.updatedAt !== expectedRevision)
            throw new Error('첨부가 바뀌었어요. 현재 목록을 확인한 뒤 다시 선택해 주세요.');
        const detach = (row, preserveRevision = false) => {
            const value = row;
            if (!value.attachmentIds?.includes(id) && value.photoBlobId !== id)
                return null;
            if ((value.petId ?? value.id) !== attachment.petId)
                throw new Error('다른 아이의 첨부 연결이 있어 삭제하지 않았어요. 원본을 확인해 주세요.');
            const next = { ...value, note: value.note?.split(' · ').includes('첨부 지움') ? value.note : [value.note, '첨부 지움'].filter(Boolean).join(' · '), updatedAt: preserveRevision ? value.updatedAt : Math.max(Date.now(), value.updatedAt + 1), ...(value.attachmentIds ? { attachmentIds: value.attachmentIds.filter(item => item !== id) } : {}) };
            if (next.photoBlobId === id)
                delete next.photoBlobId;
            return next;
        };
        for (const table of DATA_TABLES) {
            const source = db[table];
            for (const row of await source.toArray()) {
                const next = detach(row);
                if (next)
                    await source.put(next);
            }
        }
        // A later trash restore must not resurrect a dangling attachment reference.
        for (const row of await db.trash.toArray()) {
            const next = detach(row.snapshot, true);
            if (next)
                await db.trash.put({ ...row, snapshot: next });
        }
        await db.attachments.delete(id);
        return attachment.blob.size;
    });
}
